/**
 * @fileoverview Profile details dialog opened from the header's user menu.
 * @description Wraps ProfileModalContent in the shared Modal so the signed-in user's profile opens
 * in place on top of whatever page they're on. The Notifications tab's body is passed in as a
 * `notificationsTab` prop by the app layer (features may not import each other); without it the
 * dialog shows only Overview and Security. The tabbed content (and its tab state) is rendered
 * as Modal's children, which Modal stops rendering the moment `open` turns false, so closing the
 * dialog unmounts it and every reopen starts on a clean Overview tab. Backed entirely by real
 * identity APIs (GET /identity/me, change password, sessions); photo upload, profile editing and
 * preferences are not offered because the backend has no self-service endpoints for them yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import ProfileModalContent from '@/features/auth/components/ProfileModalContent'
import Modal from '@/shared/components/molecules/Modal'

import type { ReactNode } from 'react'

interface ProfileModalProps {
  open: boolean
  onClose: () => void
  notificationsTab?: ReactNode | undefined
}

/**
 * @component
 * @param {ProfileModalProps} props - Open state, close handler and the optional Notifications tab.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called on Escape, a backdrop click, or the close button.
 * @param {ReactNode} [props.notificationsTab] - Body of the Notifications tab; the tab is hidden
 * when omitted.
 */
function ProfileModal({ open, onClose, notificationsTab }: ProfileModalProps) {
  const { t } = useTranslation()

  return (
    <Modal open={open} onClose={onClose} title={t('profile.title')} data-testid="profile-modal">
      <ProfileModalContent notificationsTab={notificationsTab} />
    </Modal>
  )
}

export default ProfileModal
